import * as React from 'react';
import * as _ from "lodash";
import { SideBarComponent } from './sidebar/sidebar.component';
import { TargetSideBarComponent } from './sidebar/invest-target-sidebar.component';
import Tooltip from '../../components/common/tooltip/tooltip.component';
import styles from './goals.module.css';
import { LeadApi } from "../../apis/lead.api";
import { redirectTo } from '../../helpers/historyhelpers';
import { useLead } from '../../contexts/lead/lead.context';
import { useApp } from '../../contexts/app/app.context';
import { useGoal } from '../../contexts/goal/goal.context';
import RetirementAge from '@assetmark/goal-components/dist/components/reirement-age/retirement-age';
import MonthlyIncome from '@assetmark/goal-components/dist/components/monthly-income/monthly-income';
import RetirementIncome from '@assetmark/goal-components/dist/components/retirement-income/retirement-income';
import AnnualContribution from '@assetmark/goal-components/dist/components/annual-contribution/annual-contribution';
import InvestmentTarget from '@assetmark/goal-components/dist/components/investment-target/investment-target';
import { TAddUpdateLeadGoal } from '../../models/add-update-lead-goal';
import { useParams } from "react-router-dom";
import { TLeadGoalsResponse } from '../../models/goals-info-response';
import { useCallback, useEffect } from 'react';
import { getLastVisitedPageCategory } from '../../helpers/pagehelper';
import { getModelPerformanceInput, parseTGoalToTAddUpdateGoal } from '../../helpers/goal.helper';
import { LocalStorageUtils } from '@assetmark/ui-kit/dist/utils/local-storage.utils';
import { GOAL_ID_KEY, GOAL_KEY } from '../../constants/storage.constant';
import { ModelPerformanceInput } from '../../lib/das-api/model/modelPerformanceInput';
import { TGoal } from '@assetmark/goal-components/dist/models/goal';
import { RemoteData } from '@assetmark/ui-kit/dist/utils/remote-data.utils';

export enum EGoalType {
	Unknown = 1,
	Retirement = 2,
	GeneralSavings = 3,
}

type TGoalsProps = {
	goalsData: TLeadGoalsResponse;
	savedLeadGoalsData: RemoteData<TLeadGoalsResponse>
	onAddLeadGoal: (lead: TAddUpdateLeadGoal, currentRoute: string, lastPageVisited: string) => void;
	onUpdateLeadGoal: (goal: TAddUpdateLeadGoal, modelPerformanceInput: ModelPerformanceInput, currentRoute: string, lastPageVisited: string) => void;
	onBackButtonClick: (backScreen: string) => void;
	onResetContextState: () => void;
}

export const GoalsComponent: React.FC<TGoalsProps> = ({ goalsData, savedLeadGoalsData, onAddLeadGoal, onUpdateLeadGoal, onResetContextState }) => {
	const { subModule } =  useParams<{subModule: string}>();
	const { applicationState } = useApp();
	const { agentId, leadProfile, leadProfileId, leadStrategyId, investorNavState } = useLead();
	const { goals, updateGoalData, goalAmount } = useGoal();

	const [goalsDataFromState, setGoalsDataFromState] = React.useState(_.cloneDeep(goals));

	const storageGoalId = LocalStorageUtils.getItem<string>(GOAL_ID_KEY).getOrElse('');
	const otherIncomeSourceType = [
		{ IncomeSourceType: 'Select Income Source', IncomeSourceTypeId: 0 },
		{ IncomeSourceType: 'Social Security', IncomeSourceTypeId: 101 },
		{ IncomeSourceType: 'Pension', IncomeSourceTypeId: 102 },
		{ IncomeSourceType: 'Rental', IncomeSourceTypeId: 103 },
		{ IncomeSourceType: 'Other', IncomeSourceTypeId: 104 },
	];

	useEffect(() => {
		setGoalsDataFromState(goals);
	}, [goals]);

	useEffect(() => {
		if(goalsData.id.isSome()) {
			updateGoalData(goalsData);
			if(leadProfile.investmentInfo && leadProfile.investmentInfo.InvestmentAmount) {
				goalAmount(leadProfile.investmentInfo?.InvestmentAmount);
			}
		}
	}, [goalsData, leadProfile.investmentInfo?.InvestmentAmount]);

	// commented this and moved to App for #351
	// useEffect(() => {
	// 	if (savedLeadGoalsData.isSuccess()) {
	// 		updateGoalData(savedLeadGoalsData.value);
	// 	} else {
	// 		// console.log('Start fresh goal journey.');
	// 	}
	// },[savedLeadGoalsData]);

	const tooltipText =
		"<p style='margin-top: 30px;margin-bottom:0'>The <span class='text-blue' style='color:#4a90e2'>\"Rule of 72\"</span> is a simple way to estimate how long an investment will take to double for a given annual rate of interest. By dividing 72 by the annual <span class='text-blue' style='color:#4a90e2'>rate of return</span>, you can approximate how many years it will take to double your savings.<br></br>For example, a $100,000 investment earning 6% interest, will grow to $200,000 in about 12 years.</p>";
	const tooltipTextForAge =
		'On average, people say they are going to retire at age 66, according to a 2018 Gallup survey of American households. Only 12% of those surveyed expect to retire before the age of 60.';
	const tooltipTextForMonthlyIncome =
		'US News and World Reports references that many people will need approximately 70 to 80 percent of their pre-retirement income coming from Social Security, retirement savings and any other income.';
	const tooltipTextForRetireIncome1 =
		'US News and World Reports references that many people will need approximately 70 to 80 percent of their pre-retirement income coming from Social Security, retirement savings and any other income.';
	const tooltipTextForRetireIncome2 =
		"<p class='mb-3 tooltip-link' style='color:#337ab7'><a href='https://www.ssa.gov/OACT/quickcalc/' target='_blank' style='color:inherit' class='linkTextAnchor'>Estimate Social Security Income</a></p>When using the Social Security quick calculator, select to see your benefit estimate in &quot;today's dollars&quot;.";
	const tooltipTextForAnnualContri =
		'According to Investopedia, the amount of savings for your retirement is directly related to your age and salary. Investors in their thirties, should have the equivalent of their salary in retirement savings. Investors in their forties, should have three-times their salary invested and five-times their salary in their fifties. In your sixties, you should have six times your salary.';

	const handleOnBackButtonClick = (): void => {
		if(subModule === 'retirement_age') {
			onResetContextState();
			LeadApi.redirectToPage$.next('investment_reason');
		} else if(subModule === 'monthly_income') {
			LeadApi.redirectToPage$.next('goals/retirement_age');
		} else if(subModule === 'retirement_income') {
			LeadApi.redirectToPage$.next('goals/monthly_income');
		} else if(subModule === 'annual_contribution') {
			LeadApi.redirectToPage$.next('goals/retirement_income');
		} else if(subModule === 'investment_target') {
			onResetContextState();
			LeadApi.redirectToPage$.next('investment_reason');
		}
	};

	const investmentAmount = leadProfile.investmentInfo?.InvestmentAmount;

	const handleAddUpdateRetirementAge = (retirementAge: number) => {
		 const calculatedBirthYear = new Date().getFullYear() - leadProfile.Investor.Age;
		 const targetYear = retirementAge + calculatedBirthYear;
		 const request = parseTGoalToTAddUpdateGoal(leadProfileId, leadStrategyId, agentId, 'retirement_age', goals, storageGoalId);
		 const tGoalRequest = { ...request,  birthYear: calculatedBirthYear, targetGoalYear: targetYear, retirementAge: retirementAge };
		 if (storageGoalId) {
			onUpdateLeadGoal(
				tGoalRequest,
				getModelPerformanceInput(goals.Amount || 0, agentId, applicationState.feeId),
				'retirement_age',
				getLastVisitedPageCategory(investorNavState),
			);
		} else {
			onAddLeadGoal(tGoalRequest, 'retirement_age', getLastVisitedPageCategory(investorNavState));
		}
	};

	const handleUpdateMonthlyIncome = (monthlyIncome: number) => {
		 const request = parseTGoalToTAddUpdateGoal(leadProfileId, leadStrategyId, agentId, 'monthly_income', goals, storageGoalId);
		 const tGoalRequest = { ...request,  monthlyIncome };
		 if (storageGoalId) {
			onUpdateLeadGoal(
				tGoalRequest,
				getModelPerformanceInput(goals.Amount || 0, agentId, applicationState.feeId),
				'monthly_income',
				getLastVisitedPageCategory(investorNavState),
			);
		} else {
			onAddLeadGoal(tGoalRequest, 'monthly_income', getLastVisitedPageCategory(investorNavState));
		}
	};

	const handleUpdateRetirementIncome = (goal: TGoal) => {
		const request = parseTGoalToTAddUpdateGoal(leadProfileId, leadStrategyId, agentId, 'retirement_income', goals, storageGoalId);
		const tGoalRequest = {
			...request,
			monthlyIncomeToRetirePercent: Number(goal.MonthlyIncomeToRetirePercent),
			netRetirementMonthlyIncome: goal.NetRetirementMonthlyIncome,
			otherRetirementIncomes: goal.OtherRetirementIncomes
				? goal.OtherRetirementIncomes.map(o => ({
						otherRetirementIncomeID: (o.Id === 0 || o.Id === "0") ? "" : o.Id.toString(),
						incomeSourceTypeId: o.IncomeSourceTypeId,
						monthlyIncome: o.MonthlyIncome || 0,
				  }))
				: [],
		};
		if (storageGoalId) {
		   onUpdateLeadGoal(
			   tGoalRequest,
			   getModelPerformanceInput(goals.Amount || 0, agentId, applicationState.feeId),
			   'retirement_income',
			   getLastVisitedPageCategory(investorNavState),
		   );
	   } else {
		   onAddLeadGoal(tGoalRequest, 'retirement_income', getLastVisitedPageCategory(investorNavState));
	   }
	};

	const updateMonthlyContribution = (annualAmount: number) => {
		const request = parseTGoalToTAddUpdateGoal(leadProfileId, leadStrategyId, agentId, 'annual_contribution', goals, storageGoalId);
		 const tGoalRequest = { ...request,  annualSavings: annualAmount };
		 if (storageGoalId) {
			onUpdateLeadGoal(
				tGoalRequest,
				getModelPerformanceInput(goals.Amount || 0, agentId, applicationState.feeId),
				'annual_contribution',
				getLastVisitedPageCategory(investorNavState),
			);
		} else {
			onAddLeadGoal(tGoalRequest, 'annual_contribution', getLastVisitedPageCategory(investorNavState));
		}
	};

	const handleAddUpdateInvestmentTarget = useCallback((TargetYear: number, AnnualSavings: number, TargetAmount: number) => {
		const request = parseTGoalToTAddUpdateGoal(
			leadProfileId,
			leadStrategyId,
			agentId,
			'retirement_age',
			goals,
			storageGoalId,
		);
		
		const tGoalRequest = {
			...request,
			targetGoalYear: TargetYear,
			annualSavings: AnnualSavings * 12,
			targetGrowthAmount: TargetAmount,
		};
		
		if (storageGoalId) {
			onUpdateLeadGoal(
				tGoalRequest,
				getModelPerformanceInput(goals.Amount || 0, agentId, applicationState.feeId),
				'investment_target',
				getLastVisitedPageCategory(investorNavState),
			);
		} else {
			onAddLeadGoal(tGoalRequest, 'investment_target', getLastVisitedPageCategory(investorNavState));
		}
	}, []) ;

	const skipGoalInformation = (): void => {
		LeadApi.redirectToPage$.next('risk-profile/investment_return');
	};

	return (
		<div className={styles.aboutYouContainer}>
			<div className={styles.colFlex}>
				<div className={styles.sidebar}>
					{subModule === 'investment_target' ? (
						<TargetSideBarComponent subModule={subModule} />
					) : (
						<SideBarComponent subModule={subModule} />
					)}
				</div>

				<div className={`w-full ${styles.flexBox} ${styles.responsiveFlexBox}`}>
					{subModule === 'investment_target' && (
						<>
							<InvestmentTarget
								investmentAmount={Number(investmentAmount)}
								goal={goalsDataFromState}
								onBackButtonClick={handleOnBackButtonClick}
								saveInvestmentTarget={handleAddUpdateInvestmentTarget}
								onSkipGoal={skipGoalInformation}
							/>

							<div className={`col-sm-4 mt-5 p-0 investment-return-tooltip ${styles.responsiveToolTip}`}>
								<Tooltip heading="Do you know the Rule of 72?" text={tooltipText} />
							</div>
						</>
					)}
					{subModule === 'retirement_age' && (
						<>
							<RetirementAge
								goal={goalsDataFromState}
								age={leadProfile.Investor.Age ? leadProfile.Investor.Age : 0}
								onBackButtonClick={handleOnBackButtonClick}
								saveRetirementAge={handleAddUpdateRetirementAge}
								onSkipGoal={skipGoalInformation}
							/>

							<div className={`col-sm-4 mt-5 p-0 goal-toolip ${styles.responsiveToolTip}`}>
								<Tooltip heading="Did you know?" text={tooltipTextForAge} />
							</div>
						</>
					)}
					{subModule === 'monthly_income' && (
						<>
							<MonthlyIncome
								goal={goalsDataFromState}
								onBackButtonClick={handleOnBackButtonClick}
								saveMonthlyIncome={handleUpdateMonthlyIncome}
							/>

							<div className={`col-sm-4 mt-5 p-0 goal-toolip ${styles.responsiveToolTip}`}>
								<Tooltip text={tooltipTextForMonthlyIncome} heading="Did you know?" />
							</div>
						</>
					)}
					{subModule === 'retirement_income' && (
						<>
							<RetirementIncome
								goal={goalsDataFromState}
								otherIncomeSourceType={otherIncomeSourceType}
								onBackButtonClick={handleOnBackButtonClick}
								saveRetirementIncome={handleUpdateRetirementIncome}
							/>

							<div className={`col-sm-4 mt-5 p-0 goal-toolip ${styles.responsiveToolTip}`}>
								<Tooltip heading="Did you know?" text={tooltipTextForRetireIncome1} />
								<Tooltip
									heading="Social Security Quick Calculator"
									text={tooltipTextForRetireIncome2}
								/>
							</div>
						</>
					)}
					{subModule === 'annual_contribution' && (
						<>
							<AnnualContribution
								goal={goalsDataFromState}
								onBackButtonClick={handleOnBackButtonClick}
								saveInvestorProfile={updateMonthlyContribution}
							/>

							<div className={`col-sm-4 mt-5 p-0 goal-toolip ${styles.responsiveToolTip}`}>
								<Tooltip heading="Did you know?" text={tooltipTextForAnnualContri} />
							</div>
						</>
					)}
				</div>
			</div>
		</div>
	);
};
